import React from 'react';
import { useParams, Navigate, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AppHeader } from '../components/AppHeader';
import { AppFooter } from '../components/AppFooter';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../components/Breadcrumb';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { MapPin, Info, ShieldCheck, Utensils, FileText, Activity, Building, Users, ExternalLink, Home, Sun, DollarSign, Heart, CheckCircle2 } from 'lucide-react';
import { destinations } from '../data/destinations';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
export function DestinationDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const destination = destinations.find(d => d.slug === slug);
  if (!destination) {
    return <Navigate to="/not-found" replace data-id="element-273" />;
  }
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://affordablemedicaltourism.com/'
    }, {
      '@type': 'ListItem',
      position: 2,
      name: 'Destinations',
      item: 'https://affordablemedicaltourism.com/destinations'
    }, {
      '@type': 'ListItem',
      position: 3,
      name: destination.country,
      item: `https://affordablemedicaltourism.com/destinations/${destination.slug}`
    }]
  };
  return <div className="min-h-screen bg-white flex flex-col" data-id="element-274">
      <Helmet data-id="element-275">
        <title data-id="element-276">
          Medical Tourism in {destination.country} | Affordable Medical Tourism
        </title>
        <meta name="description" content={`Learn about medical tourism in ${destination.country}. Discover top facilities, popular procedures, illustrative savings, and travel details.`} data-id="element-277" />
        <link rel="canonical" href={`https://affordablemedicaltourism.com/destinations/${destination.slug}`} data-id="element-278" />
        <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" integrity="sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=" crossOrigin="" data-id="element-279" />
        <script type="application/ld+json" data-id="element-280">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <AppHeader data-id="element-281" />

      <main className="flex-1" data-id="element-282">
        {/* Hero Section */}
        <div className="relative h-[40vh] min-h-[400px] w-full bg-slate-900" data-id="element-283">
          <img src={destination.heroImage} alt={`${destination.country} landscape`} className="absolute inset-0 w-full h-full object-cover opacity-40" data-id="element-284" />
          <div className="absolute inset-0 flex items-center" data-id="element-285">
            <div className="max-w-7xl mx-auto px-6 md:px-12 w-full" data-id="element-286">
              <Breadcrumb className="mb-6 text-slate-300" data-id="element-287">
                <BreadcrumbList data-id="element-288">
                  <BreadcrumbItem data-id="element-289">
                    <BreadcrumbLink href="/" className="text-slate-300 hover:text-white" data-id="element-290">
                      Home
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="text-slate-500" data-id="element-291" />
                  <BreadcrumbItem data-id="element-292">
                    <BreadcrumbLink href="/destinations" className="text-slate-300 hover:text-white" data-id="element-293">
                      Destinations
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="text-slate-500" data-id="element-294" />
                  <BreadcrumbItem data-id="element-295">
                    <BreadcrumbPage className="text-white font-medium" data-id="element-296">
                      {destination.country}
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4" data-id="element-297">
                Medical Tourism in {destination.country}
              </h1>
              <div className="flex flex-wrap gap-2 mt-4" data-id="element-298">
                {destination.knownFor.map(specialty => <Badge key={specialty} variant="secondary" className="bg-white/20 text-white hover:bg-white/30 border-none" data-id="element-299">
                    {specialty}
                  </Badge>)}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-300">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12" data-id="element-301">
            <article className="prose prose-slate prose-lg max-w-none" data-id="element-302">
              {destination.content.split('\n\n').map((paragraph, idx) => <p key={idx} className="mb-6 text-slate-700 leading-relaxed" data-id="element-303">
                  {paragraph.trim()}
                </p>)}
            </article>

            {/* Facilities Section */}
            <div data-id="element-304">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-305">
                <Building className="w-6 h-6 text-blue-600" data-id="element-306" />
                Featured Facilities
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-307">
                {destination.facilities.map((facility, idx) => <div key={idx} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-id="element-308">
                    <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-309">
                      {facility.name}
                    </h3>
                    <div className="space-y-2 text-sm text-slate-600 mb-4" data-id="element-310">
                      <p data-id="element-311">
                        <strong data-id="element-312">Total Beds:</strong> {facility.totalBeds}
                      </p>
                      <p data-id="element-313">
                        <strong data-id="element-314">Key Specialties:</strong>{' '}
                        {facility.keySpecialties.join(', ')}
                      </p>
                      <p data-id="element-315">
                        <strong data-id="element-316">Free Online Consultation:</strong>{' '}
                        {facility.freeOnlineConsultation ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <a href={facility.url} className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1" data-id="element-317">
                      Visit Website <ExternalLink className="w-3 h-3" data-id="element-318" />
                    </a>
                  </div>)}
              </div>
            </div>

            {/* Facilitators Section */}
            <div data-id="element-319">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-320">
                <Users className="w-6 h-6 text-blue-600" data-id="element-321" />
                Local Facilitators
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-322">
                {destination.facilitators.map((facilitator, idx) => <div key={idx} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-id="element-323">
                    <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-324">
                      {facilitator.name}
                    </h3>
                    <div className="space-y-2 text-sm text-slate-600 mb-4" data-id="element-325">
                      <p data-id="element-326">
                        <strong data-id="element-327">Services Provided:</strong>{' '}
                        {facilitator.services.join(', ')}
                      </p>
                      <p data-id="element-328">
                        <strong data-id="element-329">Illustrative Package:</strong>{' '}
                        {facilitator.totalPackagePrice}
                      </p>
                    </div>
                    <a href={facilitator.url} className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1" data-id="element-330">
                      Visit Website <ExternalLink className="w-3 h-3" data-id="element-331" />
                    </a>
                  </div>)}
              </div>
            </div>

            {/* Global Medical Tourism Cities for Settlement */}
            {/* @ts-ignore */}
            {destination.settlementCities && destination.settlementCities.length > 0 && <div className="mt-16" data-id="element-332">
                  <div className="mb-8" data-id="element-333">
                    <h2 className="text-3xl font-bold text-slate-900 mb-4 flex items-center gap-3" data-id="element-334">
                      <Home className="w-8 h-8 text-blue-600" data-id="element-335" />
                      Global Medical Tourism Cities for Settlement
                    </h2>
                    <p className="text-lg text-slate-600" data-id="element-336">
                      Explore top cities in {destination.country} perfectly
                      suited for long-term medical stays, rehabilitation, and
                      retirement. These hubs offer exceptional healthcare
                      access, established expat communities, and a high quality
                      of life.
                    </p>
                  </div>

                  <div className="space-y-8" data-id="element-337">
                    {/* @ts-ignore */}
                    {destination.settlementCities.map((city, idx) => <div key={idx} className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-id="element-338">
                        <div className="bg-slate-50 p-6 border-b border-slate-200" data-id="element-339">
                          <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-340">
                            {city.name}
                          </h3>
                          <p className="text-slate-700" data-id="element-341">{city.whySettle}</p>
                        </div>

                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-342">
                          <div className="space-y-6" data-id="element-343">
                            <div data-id="element-344">
                              <h4 className="font-semibold text-slate-900 flex items-center gap-2 mb-2" data-id="element-345">
                                <DollarSign className="w-5 h-5 text-green-600" data-id="element-346" />
                                Cost & Budget
                              </h4>
                              <ul className="space-y-2 text-sm text-slate-600" data-id="element-347">
                                <li data-id="element-348">
                                  <strong className="text-slate-700" data-id="element-349">
                                    Cost of Living vs US:
                                  </strong>{' '}
                                  {city.costOfLivingVsUS}
                                </li>
                                <li data-id="element-350">
                                  <strong className="text-slate-700" data-id="element-351">
                                    Typical Monthly Budget:
                                  </strong>{' '}
                                  {city.typicalMonthlyBudget}
                                </li>
                              </ul>
                            </div>

                            <div data-id="element-352">
                              <h4 className="font-semibold text-slate-900 flex items-center gap-2 mb-2" data-id="element-353">
                                <Heart className="w-5 h-5 text-red-500" data-id="element-354" />
                                Healthcare & Support
                              </h4>
                              <ul className="space-y-2 text-sm text-slate-600" data-id="element-355">
                                <li data-id="element-356">
                                  <strong className="text-slate-700" data-id="element-357">
                                    Hospital Proximity:
                                  </strong>{' '}
                                  {city.hospitalProximity}
                                </li>
                                <li data-id="element-358">
                                  <strong className="text-slate-700" data-id="element-359">
                                    Assisted Living:
                                  </strong>{' '}
                                  {city.assistedLivingAvailability}
                                </li>
                              </ul>
                            </div>
                          </div>

                          <div className="space-y-6" data-id="element-360">
                            <div data-id="element-361">
                              <h4 className="font-semibold text-slate-900 flex items-center gap-2 mb-2" data-id="element-362">
                                <Sun className="w-5 h-5 text-amber-500" data-id="element-363" />
                                Lifestyle & Community
                              </h4>
                              <ul className="space-y-2 text-sm text-slate-600" data-id="element-364">
                                <li data-id="element-365">
                                  <strong className="text-slate-700" data-id="element-366">
                                    Climate:
                                  </strong>{' '}
                                  {city.climate}
                                </li>
                                <li data-id="element-367">
                                  <strong className="text-slate-700" data-id="element-368">
                                    Expat Community:
                                  </strong>{' '}
                                  {city.expatCommunity}
                                </li>
                              </ul>
                            </div>

                            <div data-id="element-369">
                              <h4 className="font-semibold text-slate-900 flex items-center gap-2 mb-2" data-id="element-370">
                                <FileText className="w-5 h-5 text-blue-600" data-id="element-371" />
                                Visa & Suitability
                              </h4>
                              <ul className="space-y-2 text-sm text-slate-600" data-id="element-372">
                                <li data-id="element-373">
                                  <strong className="text-slate-700" data-id="element-374">
                                    Long-Stay Visa:
                                  </strong>{' '}
                                  {city.longStayVisaPathway}
                                </li>
                              </ul>
                              <div className="mt-3 flex flex-wrap gap-2" data-id="element-375">
                                {/* @ts-ignore */}
                                {city.bestSuitedFor.map((suitability, sIdx) => <Badge key={sIdx} variant="secondary" className="bg-blue-50 text-blue-700 text-xs" data-id="element-376">
                                    <CheckCircle2 className="w-3 h-3 mr-1" data-id="element-377" />
                                    {suitability}
                                  </Badge>)}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-slate-50 p-4 border-t border-slate-200 flex justify-end" data-id="element-378">
                          <Button onClick={() => navigate('/contact')} data-id="element-379">
                            Inquire about settling in {city.name}
                          </Button>
                        </div>
                      </div>)}
                  </div>
                </div>}

            {/* Travel & Logistics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-16" data-id="element-380">
              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100" data-id="element-381">
                <div className="flex items-center gap-2 mb-4 text-blue-700" data-id="element-382">
                  <FileText className="w-5 h-5" data-id="element-383" />
                  <h3 className="font-semibold text-slate-900" data-id="element-384">
                    Visa Requirements
                  </h3>
                </div>
                <p className="text-sm text-slate-600" data-id="element-385">
                  {destination.visaRequirements}
                </p>
              </div>

              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100" data-id="element-386">
                <div className="flex items-center gap-2 mb-4 text-blue-700" data-id="element-387">
                  <Utensils className="w-5 h-5" data-id="element-388" />
                  <h3 className="font-semibold text-slate-900" data-id="element-389">Food Options</h3>
                </div>
                <p className="text-sm text-slate-600" data-id="element-390">
                  {destination.foodOptions}
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8" data-id="element-391">
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100" data-id="element-392">
              <h3 className="text-xl font-bold text-slate-900 mb-6" data-id="element-393">
                Destination Overview
              </h3>

              <div className="space-y-6" data-id="element-394">
                <div data-id="element-395">
                  <div className="text-sm text-slate-500 mb-1 flex items-center gap-1" data-id="element-396">
                    <Info className="w-4 h-4" data-id="element-397" /> Illustrative Savings
                  </div>
                  <div className="text-3xl font-bold text-green-700" data-id="element-398">
                    {destination.illustrativeSavingsRange}
                  </div>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-399">
                    Compared to US prices
                  </p>
                </div>

                <div className="pt-4 border-t border-slate-200" data-id="element-400">
                  <div className="flex items-center gap-2 mb-3 text-blue-700" data-id="element-401">
                    <Activity className="w-5 h-5" data-id="element-402" />
                    <h4 className="font-semibold text-slate-900" data-id="element-403">
                      Popular Procedures
                    </h4>
                  </div>
                  <ul className="list-disc pl-5 space-y-1 text-slate-600 text-sm" data-id="element-404">
                    {destination.popularProcedures.map(proc => <li key={proc} data-id="element-405">{proc}</li>)}
                  </ul>
                </div>

                <div className="pt-4 border-t border-slate-200" data-id="element-406">
                  <div className="flex items-center gap-2 mb-3 text-blue-700" data-id="element-407">
                    <ShieldCheck className="w-5 h-5" data-id="element-408" />
                    <h4 className="font-semibold text-slate-900" data-id="element-409">
                      Accreditations & Stats
                    </h4>
                  </div>
                  <ul className="list-disc pl-5 space-y-1 text-slate-600 text-sm mb-3" data-id="element-410">
                    {destination.accreditations.map(acc => <li key={acc} data-id="element-411">{acc}</li>)}
                  </ul>
                  <div className="space-y-2 text-sm text-slate-600" data-id="element-412">
                    <p data-id="element-413">
                      <strong data-id="element-414">Stats:</strong> {destination.hospitalStats}
                    </p>
                    {/* @ts-ignore */}
                    {destination.patientVolume && <p data-id="element-415">
                        {/* @ts-ignore */}
                        <strong data-id="element-416">Patient Volume:</strong>{' '}
                        {destination.patientVolume}
                      </p>}
                    {/* @ts-ignore */}
                    {destination.primaryReason && <p data-id="element-417">
                        {/* @ts-ignore */}
                        <strong data-id="element-418">Primary Reason:</strong>{' '}
                        {destination.primaryReason}
                      </p>}
                  </div>
                </div>

                <Button className="w-full mt-4" size="lg" onClick={() => navigate('/contact')} data-id="element-419">
                  Inquire about {destination.country}
                </Button>
              </div>
            </div>

            {/* Map */}
            <div className="bg-white p-2 rounded-2xl shadow-sm border border-slate-200 h-[300px] relative z-0" data-id="element-420">
              <MapContainer center={destination.coordinates} zoom={5} scrollWheelZoom={false} style={{
              height: '100%',
              width: '100%',
              borderRadius: '0.75rem'
            }} data-id="element-421">
                <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-422" />
                <Marker position={destination.coordinates} data-id="element-423">
                  <Popup data-id="element-424">Medical hub in {destination.country}</Popup>
                </Marker>
              </MapContainer>
            </div>
          </div>
        </div>
      </main>

      <AppFooter data-id="element-425" />
    </div>;
}